import { difference, throttle } from 'es-toolkit'
import { useEffect, useMemo, useRef, useState } from 'react'

import PriceWebSocket from '@/libs/reliable-websocket/PriceWebSocket'
import {
  type RawCoinMarketMessage,
  transformRawCoinMarketMessage,
} from '@/libs/reliable-websocket/PriceWebSocket/utils'
import type { CoinSlug, LiveCoinMarket } from '@/types/coin'

type Callbacks = {
  updateLiveCoinsMarket: (next: Record<CoinSlug, LiveCoinMarket>) => void
  clearLiveCoinsMarket: () => void
}

const emptySlugs: CoinSlug[] = []

export default function useSubscribeLiveCoinsMarket(
  slugs: CoinSlug[],
  { updateLiveCoinsMarket, clearLiveCoinsMarket }: Callbacks,
  throttleWait = 2000,
) {
  const wsRef = useRef<PriceWebSocket | null>(null)
  const [wsCreated, setWsCreated] = useState(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    wsRef.current = new PriceWebSocket()
    setWsCreated(true)
    return () => {
      isMountedRef.current = false
      wsRef.current?.close()
    }
  }, [])

  const cachedLiveCoinsMarket = useRef<Record<CoinSlug, LiveCoinMarket>>({})

  const updateLiveCoinMarketThrottled = useMemo(
    () =>
      throttle(() => {
        if (isMountedRef.current) {
          updateLiveCoinsMarket({ ...cachedLiveCoinsMarket.current })
        }
      }, throttleWait),
    [throttleWait, updateLiveCoinsMarket],
  )

  const [subscribeSlugs, setSubscribeSlugs] = useState<CoinSlug[]>(
    slugs.length > 0 ? slugs : emptySlugs,
  )
  useEffect(() => {
    // Update subscribe slugs only when slugs change
    setSubscribeSlugs(prevSlugs => {
      if (slugs.length === 0) return emptySlugs
      const diffSlugs = difference(slugs, prevSlugs)
      return diffSlugs.length > 0 ? slugs : prevSlugs
    })
  }, [slugs])

  useEffect(() => {
    if (subscribeSlugs.length === 0 || !wsRef.current) return

    wsRef.current.subscribe<RawCoinMarketMessage>(
      { slugs: subscribeSlugs },
      rawCoinMarketMessage => {
        if (!rawCoinMarketMessage || !rawCoinMarketMessage.slug) return

        // Merge new live coin market data with cached one or create a new one, then update the cached live coins market
        const currentLiveCoinMarket = cachedLiveCoinsMarket.current[rawCoinMarketMessage.slug] ?? {}
        const newLiveCoinMarket: LiveCoinMarket =
          transformRawCoinMarketMessage(rawCoinMarketMessage)
        const nextLiveCoinsMarket = {
          ...currentLiveCoinMarket,
          ...newLiveCoinMarket,
        }
        cachedLiveCoinsMarket.current[rawCoinMarketMessage.slug] = nextLiveCoinsMarket

        updateLiveCoinMarketThrottled()
      },
    )

    return () => {
      // Clean up previous live coins market data when slugs change
      clearLiveCoinsMarket()
      cachedLiveCoinsMarket.current = {}
      wsRef.current?.unsubscribe()
    }
  }, [subscribeSlugs, updateLiveCoinMarketThrottled, wsCreated, clearLiveCoinsMarket])
}
